import { useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, X, Plus } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar, photoSrc } from "@/components/Avatar";
import { VideoUploader } from "@/components/video/VideoUploader";
import { DestinationPicker, EMPTY_DESTS, needsWiderConfirm, summarize } from "@/components/post/DestinationPicker";
import { WiderAudienceDialog, resultToast } from "@/components/post/WiderAudienceDialog";

const MAX = 6;

export function PostComposer({ onPosted, label = "Criar publicação" }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [images, setImages] = useState([]);
  const [video, setVideo] = useState(null);
  const [dests, setDests] = useState(EMPTY_DESTS);
  const [legacy, setLegacy] = useState(false); // «Onde publicar?» desligado → comportamento anterior (perfil + feed), sem campo de destinos
  const [comms, setComms] = useState([]);
  const [confirmPriv, setConfirmPriv] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, MAX - images.length);
    e.target.value = "";
    setBusy(true);
    for (const file of files) {
      if (!file.type.startsWith("image/")) { toast.error("Apenas imagens são permitidas"); continue; }
      try {
        const fd = new FormData(); fd.append("file", file); fd.append("purpose", "profile_post");
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setImages((im) => [...im, data.url]);
      } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Erro no upload"); }
    }
    setBusy(false);
  };

  const reset = () => { setText(""); setImages([]); setVideo(null); setDests(EMPTY_DESTS); setOpen(false); };
  const hasContent = text.trim() || images.length > 0 || video;
  const hasDest = legacy || dests.profile || dests.feed || dests.communities.length > 0;

  const send = async (confirm = false) => {
    setBusy(true);
    try {
      const body = legacy ? { text, images, video_id: video?.id || null } : { text, images, video_id: video?.id || null, destinations: dests, confirm_wider_audience: confirm };
      const { data } = await api.post("/profile/posts", body);
      if (legacy) toast.success("Publicado"); else resultToast(toast, data, summarize(dests, comms));
      reset(); onPosted?.(data);
    } catch (err) {
      const d = err.response?.data?.detail;
      if (d?.code === "CONFIRM_WIDER_AUDIENCE") setConfirmPriv(d.private_communities || []);
      else toast.error(formatApiError(d) || "Erro ao publicar");
    } finally { setBusy(false); }
  };
  const submit = () => {
    if (legacy) { send(false); return; }
    if (!hasDest) { toast.error("Escolhe pelo menos um destino"); return; }
    const priv = needsWiderConfirm(dests, comms);
    if (priv) { setConfirmPriv(priv.map((c) => c.name)); return; }
    send(false);
  };

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} data-testid="profile-post-open"
              className="w-full flex items-center gap-3 bg-white rounded-2xl border border-zinc-200 p-3 text-left hover:border-zinc-400 transition-colors">
        <Avatar src={user.photo_url} name={user.name} size={36} />
        <span className="text-sm text-zinc-500 flex-1">Partilhar uma atualização…</span>
        <span className="text-xs font-semibold px-3 py-1.5 rounded-full bg-[#c8102e] text-white inline-flex items-center gap-1 shrink-0"><Plus size={12} /> {label}</span>
      </button>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-zinc-300 p-3" data-testid="profile-post-composer">
      <div className="flex items-start gap-3">
        <Avatar src={user.photo_url} name={user.name} size={36} />
        <textarea autoFocus value={text} onChange={(e) => setText(e.target.value)} maxLength={4000} rows={4} placeholder="Partilhar uma atualização…"
                  data-testid="profile-post-text" className="flex-1 text-sm resize-none outline-none placeholder:text-zinc-400 min-h-[88px]" />
      </div>
      {images.length > 0 && (
        <div className="grid grid-cols-3 gap-2 mt-2" data-testid="profile-post-images-preview">
          {images.map((url, i) => (
            <div key={url} className="relative aspect-square rounded-lg overflow-hidden border bg-zinc-100">
              <img src={photoSrc(url)} alt="" className="w-full h-full object-cover" />
              <button type="button" onClick={() => setImages(images.filter((_, j) => j !== i))} aria-label="Remover imagem" data-testid={`profile-post-remove-image-${i}`}
                      className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1"><X size={12} /></button>
            </div>
          ))}
        </div>
      )}
      <DestinationPicker value={dests} onChange={setDests} onLoaded={setComms} onDisabled={() => setLegacy(true)} />
      <div className="flex items-center justify-between mt-3 gap-2 flex-wrap">
        <button type="button" onClick={() => inputRef.current?.click()} disabled={busy || images.length >= MAX} data-testid="profile-post-add-image"
                className="inline-flex items-center gap-1 text-xs font-semibold text-zinc-700 disabled:opacity-40">
          <ImagePlus size={16} /> Imagens ({images.length}/{MAX})
        </button>
        <VideoUploader value={video} onChange={setVideo} disabled={busy} testid="profile-post-video" />
        <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" onChange={upload} data-testid="profile-post-image-input" />
        <div className="flex gap-2 ml-auto">
          <button type="button" onClick={reset} disabled={busy} data-testid="profile-post-cancel" className="px-3 py-1.5 rounded-xl border border-zinc-300 text-sm font-semibold">Cancelar</button>
          <button type="button" onClick={submit} disabled={busy || !hasContent || !hasDest} data-testid="profile-post-submit"
                  className="px-4 py-1.5 rounded-xl bg-[#c8102e] hover:bg-[#a50c24] text-white text-sm font-semibold disabled:opacity-40">{busy ? "A publicar..." : "Publicar"}</button>
        </div>
      </div>
      <WiderAudienceDialog privateNames={confirmPriv} onCancel={() => setConfirmPriv(null)} onConfirm={() => { setConfirmPriv(null); send(true); }} />
    </div>
  );
}
